import React, { useCallback, useEffect } from 'react'
import * as L from 'leaflet'
import * as turf from '@turf/turf'
import { useLeafletContext } from '@react-leaflet/core'
import { PolygonItem, Polygons } from 'components/MapV2/components/ui/Polygon'
import { Polygon } from 'leaflet'
import { MapMarkerProps } from '../../ui'

export type ControlledDrawingsProps = {
  items: PolygonItem[] | MapMarkerProps[]
  onChange?: (items: PolygonItem[] | MapMarkerProps[]) => void
  pathOptions?: L.PathOptions
}

export const getCircleDrawing = (item: any) => {
  if (!item) {
    return { positions: [] }
  }
  try {
    const { lat, lng } = item.getLatLng()
    const center = [lng, lat]

    const radius = item.getRadius()

    const options = { steps: 64, units: 'meters' as turf.Units }
    const circle = turf.circle(center, radius, options)

    return {
      positions: circle.geometry.coordinates[0].map(([lon, lat]) => [lat, lon]),
    }
  } catch (error) {
    console.error(error)
    return { positions: [] }
  }
}

export const ControlledDrawings: React.FC<React.PropsWithChildren<ControlledDrawingsProps>> = ({
  items,
  pathOptions,
  onChange,
}) => {
  const { map } = useLeafletContext()

  const getDrawing = (item: Polygon) => {
    const geojson = item.toGeoJSON()

    return {
      positions: geojson.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]) as any,
    }
  }

  const onUpdate = useCallback(() => {
    if (onChange) {
      const items = map.pm.getGeomanLayers() as Polygon[]

      onChange(
        items.map((item, index) => {
          const shape = item.pm.getShape()

          if (shape === 'Circle') {
            return { ...getCircleDrawing(item), id: index }
          }
          if (shape === 'Marker') {
            const geojson = item.toGeoJSON()
            return {
              id: index,
              marker: {
                position: [geojson.geometry.coordinates[1], geojson.geometry.coordinates[0]],
              },
            } as any
          }
          return { ...getDrawing(item), id: index }
        }),
      )
    }
  }, [onChange])

  const onCreate = useCallback(
    (e: { layer: { remove: () => void } }) => {
      if (onChange) {
        onUpdate()
        e.layer.remove()
        map.pm.disableDraw('Marker')
      }
    },
    [onUpdate],
  )

  useEffect(() => {
    if (items.length > 0) {
      const geomanLayers = map.pm.getGeomanLayers()

      geomanLayers.forEach((layer) => {
        layer.on('pm:edit', onUpdate)
        layer.on('pm:remove', onUpdate)
      })

      return () => {
        geomanLayers.forEach((layer) => {
          layer.off('pm:edit', onUpdate)
          layer.off('pm:remove', onUpdate)
        })
      }
    }
  }, [items, onUpdate])

  useEffect(() => {
    map.on('pm:create', onCreate)

    return () => {
      map.off('pm:create', onCreate)
    }
  }, [map, onCreate])

  const manipulatedItems = pathOptions
    ? items.map((item) => ({
        ...pathOptions,
        ...item,
      }))
    : items

  const isMarker = (items[0] as MapMarkerProps)?.marker?.position

  return !isMarker ? (
    <Polygons polygons={manipulatedItems as PolygonItem[]} pmIgnore={false} />
  ) : null
}
